import { capitalize, max, sum } from 'lodash'

import { TOP_VALUES_LIMIT } from 'modules/performance_analytics/components/helpers'

import type { ChartDataItem } from '../types'

export interface KeyCountPair {
  readonly key: string
  readonly count: number
}

export const OTHER_CATEGORY_KEY = 'Other'

export const normalizeGroupValue = (value: string): string => {
  return capitalize(value.trim())
}

export const groupKeyCountPairs = (
  sortedPairs: readonly KeyCountPair[]
): {
  topKeys: readonly KeyCountPair[]
  otherCount: number
} => {
  const topKeys = sortedPairs.slice(0, TOP_VALUES_LIMIT)
  const otherKeys = sortedPairs.slice(TOP_VALUES_LIMIT)
  const otherCount = sum(otherKeys.map((item) => item.count))

  return { topKeys, otherCount }
}

export const consolidateKeyCountPairs = (keyCountPairs: KeyCountPair[]): KeyCountPair[] => {
  const groupedPairs = new Map<string, number>()

  keyCountPairs.forEach(({ key, count }) => {
    const existingCount = groupedPairs.get(key) || 0
    groupedPairs.set(key, existingCount + count)
  })

  return Array.from(groupedPairs.entries()).map(([key, count]) => ({
    key,
    count,
  }))
}

export const sortPairsByCount = (pairs: KeyCountPair[]): KeyCountPair[] => {
  return pairs.sort((a, b) => b.count - a.count)
}

export const calculateTotalStackedCount = (stackedChartData: readonly ChartDataItem[]): number => {
  return stackedChartData.reduce((total, item) => {
    const numericValues = Object.values(item).filter(
      (value): value is number => typeof value === 'number'
    )
    return total + sum(numericValues)
  }, 0)
}

export const calculateMaxStackedValue = (stackedChartData: readonly ChartDataItem[]): number => {
  const bucketSums = stackedChartData.map((item) => {
    const numericValues = Object.values(item).filter(
      (value): value is number => typeof value === 'number'
    )
    return sum(numericValues)
  })

  return max(bucketSums) || 0
}

export const extractFinalKeys = (stackedChartData: readonly ChartDataItem[]): string[] => {
  const allKeys = stackedChartData.flatMap((item) =>
    Object.keys(item).filter((key) => key !== 'bucket')
  )

  return Array.from(new Set(allKeys))
}
